import React, { FunctionComponent } from "react";
import {
  MedigapPlan,
  MedigapPlanType,
  usePartBDeductible,
} from "../utils/plans";
import { useTranslate } from "../../../helpers";
import PlanDetailsTable from "./PlanDetailsTable";
import ExclamationNote from "./ExclamationNote";

interface PartBServicesProps {
  planType: MedigapPlanType;
  planData: MedigapPlan;
}

const PartBServices: FunctionComponent<PartBServicesProps> = ({
  planData,
  planType,
}) => {
  const t = useTranslate();
  const title = t("medigap.plan_details.what_you_will_pay_part_b");
  const partBWIHighDeductibleFootnote = usePartBDeductible(
    "medigap.plan_details.partb_wi_high_deductible_footnote.interpolated"
  );
  const partBOnlyOnceFootnote = usePartBDeductible(
    "medigap.plan_details.part_b_deductible_only_once.interpolated"
  );
  const data = [
    {
      property: t("medigap.plan_details.covered_part_b_services"),
      tooltip: t("medigap.plan_details.covered_part_b_services_tooltip"),
      value: [
        {
          property: t("medigap.plan_details.first"),
          value: t(planData.covered_medical_services_first),
        },
        {
          property: t("medigap.plan_details.then"),
          value: t(planData.covered_medical_services_then),
        },
      ],
    },
    planData.preventive_services
      ? {
          property: t("medigap.plan_details.preventive_services"),
          tooltip: t("medigap.plan_details.preventive_services_tooltip"),
          value: t(planData.preventive_services),
          hide:
            planData.state === "MN" &&
            ![
              MedigapPlanType.MN_SUPP_50,
              MedigapPlanType.MN_SUPP_75,
              MedigapPlanType.MN_N,
              MedigapPlanType.MN_M,
            ].includes(planType),
        }
      : null,

    planData.prev_serv_cover
      ? {
          property: t("medigap.plan_details.preventive_services_covered"),
          tooltip: t("medigap.plan_details.preventive_services_tooltip"),
          value: t(planData.prev_serv_cover),
          hide:
            planData.state === "MN" &&
            ![
              MedigapPlanType.MN_BASIC,
              MedigapPlanType.MN_EXT_BASIC_NEW,
              MedigapPlanType.MN_HIGH_F_NEW,
              MedigapPlanType.MN_EXT_BASIC,
              MedigapPlanType.MN_HIGH_F,
            ].includes(planType),
        }
      : null,
    planData.prev_serv_non
      ? {
          property: t("medigap.plan_details.preventive_services_not_covered"),
          value: t(planData.prev_serv_non),
          hide:
            planData.state === "MN" &&
            ![
              MedigapPlanType.MN_BASIC,
              MedigapPlanType.MN_EXT_BASIC_NEW,
              MedigapPlanType.MN_HIGH_F_NEW,
              MedigapPlanType.MN_EXT_BASIC,
              MedigapPlanType.MN_HIGH_F,
            ].includes(planType),
        }
      : null,
    {
      property: t("medigap.plan_details.part_b_excess_charges"),
      tooltip: t("medigap.plan_details.part_b_excess_charges_tooltip"),
      value: t(planData.excess_charges),
    },
    {
      property: t("medigap.plan_details.blood_outside_stay"),
      value: [
        {
          property: t("medigap.plan_details.blood_first_3"),
          value: t(planData.b_blood_first_3),
        },
        {
          property: t("medigap.plan_details.blood_after_3"),
          value: t(planData.b_blood_after_3),
        },
      ],
    },
    planData.tests_diagnostics
      ? {
          property: t("medigap.plan_details.diagnostic_tests"),
          tooltip: t("medigap.plan_details.diagnostic_tests_tooltip"),
          value: t(planData.tests_diagnostics),
        }
      : null,
    planData.outpatient_biological_mental_covered_first &&
    planData.outpatient_biological_mental_covered_then
      ? {
          property: t("medigap.plan_details.ma.outpatient_mental_bio_covered"),
          value: [
            {
              property: t("medigap.plan_details.first"),
              value: t(planData.outpatient_biological_mental_covered_first),
            },
            {
              property: t("medigap.plan_details.then"),
              value: t(planData.outpatient_biological_mental_covered_then),
            },
          ],
          hide: planData.state !== "MA",
        }
      : null,
    planData.outpatient_biological_mental_not_covered
      ? {
          property: t(
            "medigap.plan_details.ma.outpatient_mental_bio_not_covered"
          ),
          value: t(planData.outpatient_biological_mental_not_covered),
          hide: planData.state !== "MA",
        }
      : null,
    planData.phys_ther
      ? {
          property: t("medigap.plan_details.mn.physical_therapy"),
          value: t(planData.phys_ther),
          hide: planData.state !== "MN",
        }
      : null,
    planData.op_mental_health
      ? {
          property: t("medigap.plan_details.mn.outpatient_mental"),
          value: t(planData.op_mental_health),
          hide: planData.state !== "MN",
        }
      : null,
    planData.outpatient_other_mental_covered_first &&
    planData.outpatient_other_mental_covered_then
      ? {
          property: t(
            "medigap.plan_details.ma.outpatient_mental_other_covered"
          ),
          value: [
            {
              property: t("medigap.plan_details.first"),
              value: t(planData.outpatient_other_mental_covered_first),
            },
            {
              property: t("medigap.plan_details.then"),
              value: t(planData.outpatient_other_mental_covered_then),
            },
          ],
          hide: planData.state !== "MA",
        }
      : null,
    planData.outpatient_other_mental_not_covered_1_24_visits &&
    planData.outpatient_other_mental_not_covered_25_plus_visits
      ? {
          property: t(
            "medigap.plan_details.ma.outpatient_mental_other_not_covered"
          ),
          value: [
            {
              property: t(
                "medigap.plan_details.ma.outpatient_mental_other_not_covered.visits_1_24"
              ),
              value: t(
                planData.outpatient_other_mental_not_covered_1_24_visits
              ),
            },
            {
              property: t(
                "medigap.plan_details.ma.outpatient_mental_other_not_covered.after_24"
              ),
              value: t(
                planData.outpatient_other_mental_not_covered_25_plus_visits
              ),
            },
          ],
          hide: planData.state !== "MA",
        }
      : null,
    {
      property: t("medigap.plan_details.ma.state_mandated_covered"),
      tooltip: t("medigap.plan_details.ma.state_mandated_covered_tooltip"),
      value: [
        planData.state_mandate_covered_first
          ? {
              property: t("medigap.plan_details.first"),
              value: t(planData.state_mandate_covered_first),
            }
          : null,
        planData.state_mandate_covered_then
          ? {
              property: t("medigap.plan_details.then"),
              value: t(planData.state_mandate_covered_then),
            }
          : null,
      ],
      hide: planData.state !== "MA",
    },
    planData.state_mandate_not_covered
      ? {
          property: t("medigap.plan_details.ma.state_mandated_not_covered"),
          value: t(planData.state_mandate_not_covered),
          hide: planData.state !== "MA",
        }
      : null,
  ];

  const footerNotes = [
    {
      id: "part-b-deductible-only-once",
      text:
        (planData.state === "MN" &&
          ![MedigapPlanType.MN_HIGH_F, MedigapPlanType.MN_EXT_BASIC].includes(
            planType
          )) ||
        [
          MedigapPlanType.A,
          MedigapPlanType.B,
          MedigapPlanType.D,
          MedigapPlanType.G,
          MedigapPlanType.HIGH_G,
          MedigapPlanType.K,
          MedigapPlanType.L,
          MedigapPlanType.M,
          MedigapPlanType.N,
          MedigapPlanType.MA_CORE,
          MedigapPlanType.MA_SUPP_1A,
          MedigapPlanType.WI_BASIC,
          MedigapPlanType.WI_25_COST,
          MedigapPlanType.WI_50_COST,
        ].includes(planType)
          ? partBOnlyOnceFootnote
          : "",
    },
    {
      id: "partb-wi-high-deductible",
      text:
        planType === MedigapPlanType.WI_HIGH_DEDUCTIBLE ? (
          <ExclamationNote message={partBWIHighDeductibleFootnote} />
        ) : (
          ""
        ),
    },
  ];

  return <PlanDetailsTable table={{ title, data, footerNotes }} />;
};

export default PartBServices;
